import RewardManager from "./RewardManager";

interface BundleProduct {
  id: string;
  title: string;
  price: string;
  quantity: number;
  percentage_discount?: string;
  inCart?: boolean;
  image?: string;
}

interface SingleCartAddData {
  id: string;
  quantity: number;
  sections?: string;
  properties?: { [key: string]: string };
}

class BYOB {
  private steps: NodeListOf<Element>;
  private stepImgs: NodeListOf<Element>;
  private stepCollections: NodeListOf<Element>;
  private bundlePriceEl: HTMLElement | null;
  private comparedPriceEl: HTMLElement | null;
  private bundleProducts: BundleProduct[] = [];
  private maxSteps: number;

  private atcButtonEl: HTMLElement | null;
  private isSubmittingBundle: boolean = false;
  private productsSwiperEl: HTMLElement | null;

  private stepPlaceholderSrcs: string[] = [];

  constructor() {
    this.steps = document.querySelectorAll(".byob-page__step");
    this.stepImgs = document.querySelectorAll(".byob-page__step-img");
    this.stepCollections = document.querySelectorAll(".byob-page__products");
    this.bundlePriceEl = document.querySelector(
      ".byob-page__price--bundle .byob-page__price-value"
    );
    this.comparedPriceEl = document.querySelector(
      ".byob-page__price--compared .byob-page__price-value"
    );
    this.maxSteps = this.steps.length;

    this.atcButtonEl = document.querySelector(".btn-atc-bundles") as
      | HTMLElement
      | null;

    this.productsSwiperEl = document.querySelector(".byob-products-swiper") as
      | HTMLElement
      | null;

    this.init();
  }

  private init(): void {
    this.stepPlaceholderSrcs = Array.from(this.stepImgs).map((el) => {
      const img = el.querySelector("img") as HTMLImageElement | null;
      return (
        img?.getAttribute("data-placeholder-src") ||
        img?.getAttribute("src") ||
        ""
      );
    });

    this.showStep(0);
    this.updateBundlePrices();
    this.updateStepTitles();
    this.setupEventListeners();
    this.setupGlobalMethods();
    this.updateATCButtonVisibility();
    this.updateProductsSwiperVisibility();
    this.initBundleContents();
  }

  private isBundleComplete(): boolean {
    return (
      this.bundleProducts.length === this.maxSteps &&
      this.bundleProducts.every(Boolean)
    );
  }

  private hasAnyNotInCart(): boolean {
    return this.bundleProducts.some((p) => p && !p.inCart);
  }

  private updateATCButtonVisibility(): void {
    if (!this.atcButtonEl) return;

    const shouldShow =
      this.isBundleComplete() &&
      this.hasAnyNotInCart() &&
      !this.isSubmittingBundle;

    this.atcButtonEl.style.display = shouldShow ? "block" : "none";
  }

  public getCurrentStepIndex(): number {
    return this.bundleProducts.length;
  }

  private updateProductsSwiperVisibility(): void {
    if (!this.productsSwiperEl) return;

    const shouldHide = this.isBundleComplete();
    this.productsSwiperEl.style.display = shouldHide ? "none" : "flex";
  }

  private setATCButtonLoadingState(isLoading: boolean): void {
    if (!this.atcButtonEl) return;

    if (isLoading) {
      this.atcButtonEl.classList.add("loading");
      this.atcButtonEl.setAttribute("aria-busy", "true");
      this.atcButtonEl.style.pointerEvents = "none";
      this.atcButtonEl.style.opacity = "0.7";
    } else {
      this.atcButtonEl.classList.remove("loading");
      this.atcButtonEl.removeAttribute("aria-busy");
      this.atcButtonEl.style.pointerEvents = "";
      this.atcButtonEl.style.opacity = "";
    }
  }

  private async addBYOBProduct(
    variantId: string,
    quantity: number,
    percentageDiscount?: string,
    inCart: boolean = false,
    itemIndex?: number
  ): Promise<any> {
    if (inCart) return Promise.resolve();

    const safeQty = Number.isFinite(quantity) && quantity > 0 ? quantity : 1;

    const cartAddData: SingleCartAddData = {
      id: variantId,
      quantity: safeQty,
      sections: "ajax-side-cart,ajax-cart-icon",
    };

    if (
      window.location.pathname === "/pages/byob" &&
      percentageDiscount &&
      percentageDiscount !== ""
    ) {
      cartAddData.properties = {
        byob: "true",
        percentage_discount: percentageDiscount || "0",
        _bundle_item: itemIndex ? itemIndex.toString() : Date.now().toString(),
      };
    }

    const response = await fetch("/cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(cartAddData),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response.json();
  }

  private updateBundlePrices(): void {
    let bundleTotal = 0;
    let comparedTotal = 0;

    this.bundleProducts.forEach((product) => {
      const priceString = product.price
        ? product.price.toString().replace(/[^0-9.]/g, "")
        : "0";
      const price = parseFloat(priceString) || 0;
      const discount = product.percentage_discount
        ? parseFloat(product.percentage_discount)
        : null;

      const qty =
        Number.isFinite(product.quantity) && product.quantity > 0
          ? product.quantity
          : 1;

      comparedTotal += price * qty;

      if (discount && discount > 0) {
        bundleTotal += price * (1 - discount / 100) * qty;
      } else {
        bundleTotal += price * qty;
      }
    });

    if (this.bundlePriceEl) {
      this.bundlePriceEl.textContent = `$${bundleTotal.toFixed(2)}`;
    }

    if (this.comparedPriceEl) {
      if (comparedTotal > bundleTotal) {
        this.comparedPriceEl.textContent = `$${comparedTotal.toFixed(2)}`;
      } else {
        this.comparedPriceEl.textContent = "";
      }
    }
  }

  private resetBundleUI(): void {
    this.bundleProducts = [];
    this.showStep(0);
    this.updateBundlePrices();
    this.updateStepTitles();
    this.updateATCButtonVisibility();
    this.updateProductsSwiperVisibility();
  }

  private showStep(stepIndex: number): void {
    this.steps.forEach((step, i) => {
      step.classList.toggle("active", i === stepIndex);
      step.classList.toggle("selected", i < this.bundleProducts.length);

      const stepImg = this.stepImgs[i] as HTMLElement;
      if (stepImg) {
        stepImg.style.opacity = i < this.bundleProducts.length ? "1" : "0.5";
        stepImg.style.filter =
          i < this.bundleProducts.length ? "none" : "grayscale(1)";

        const imgEl = stepImg.querySelector("img") as HTMLImageElement | null;
        if (imgEl) {
          const selected = this.bundleProducts[i];
          const newSrc = selected?.image
            ? selected.image
            : this.stepPlaceholderSrcs[i];

          if (newSrc) {
            const currentSrc =
              imgEl.getAttribute("src") || imgEl.currentSrc || "";
            if (currentSrc !== newSrc) imgEl.setAttribute("src", newSrc);
          }
        }
      }

      const closeBtn = step.querySelector(".byob-step-close") as HTMLElement;
      if (closeBtn) {
        closeBtn.style.display =
          i < this.bundleProducts.length ? "block" : "none";
      }
    });

    if (this.stepCollections.length > 0) {
      const productsContainer = this.stepCollections[0] as HTMLElement;
      productsContainer.style.display = "block";
    }

    this.updateStepTitles();
    this.updateATCButtonVisibility();
    this.updateProductsSwiperVisibility();
  }

  private updateStepTitles(): void {
    const stepTitles = document.querySelectorAll("[data-step-title]");
    const selectedCount = this.bundleProducts.length;
    const remaining = this.maxSteps - selectedCount;

    stepTitles.forEach((titleEl) => {
      const textEl = titleEl.querySelector(".step-title-text");
      if (!textEl) return;

      let newText = "";

      if (selectedCount === 0) {
        newText = `Step 1: Choose ${this.maxSteps} Bull Shots`;
      } else if (selectedCount === 1) {
        newText = `Step 2: Choose ${remaining} Bull Shots`;
      } else if (selectedCount === 2) {
        newText = `Step 3: Choose ${remaining} Bull Shot`;
      } else if (selectedCount >= this.maxSteps) {
        newText = "Your Bundle is Complete";
      }

      textEl.textContent = newText;
    });

    this.updateATCButtonVisibility();
  }

  private setButtonLoadingState(button: HTMLElement, isLoading: boolean): void {
    if (isLoading) {
      button.classList.add("loading");
      button.setAttribute("disabled", "true");
    } else {
      button.classList.remove("loading");
      button.removeAttribute("disabled");
    }
  }

  private setAllButtonsLoadingState(isLoading: boolean): void {
    const allButtons = document.querySelectorAll(".byob-product-card__btn");
    allButtons.forEach((button) => {
      this.setButtonLoadingState(button as HTMLElement, isLoading);
    });
  }

  private async submitBundleToCart(): Promise<void> {
    if (this.isSubmittingBundle) return;
    if (!this.isBundleComplete()) return;

    this.isSubmittingBundle = true;
    this.updateATCButtonVisibility();
    this.setATCButtonLoadingState(true);
    this.setAllButtonsLoadingState(true);

    try {
      let globalItemCounter = 0;

      for (let i = 0; i < this.bundleProducts.length; i++) {
        const product = this.bundleProducts[i];
        if (!product) continue;

        const { id, percentage_discount, inCart, quantity } = product;

        const itemsToAdd = quantity || 1;

        for (let q = 0; q < itemsToAdd; q++) {
          globalItemCounter++;

          await this.addBYOBProduct(
            id,
            1,
            percentage_discount,
            inCart,
            globalItemCounter
          );

          if (q < itemsToAdd - 1) {
            await new Promise((resolve) => setTimeout(resolve, 200));
          }
        }

        if (i < this.bundleProducts.length - 1) {
          await new Promise((resolve) => setTimeout(resolve, 300));
        }
      }

      const sideCartElement = document.querySelector("side-cart") as any;
      if (sideCartElement) {
        await RewardManager.evaluateAndUpdateRewards();
        await sideCartElement.refreshSideCart();
        sideCartElement.open("byob-bundle-complete");
      }

      this.bundleProducts = [];
      this.showStep(0);
      this.updateBundlePrices();
    } catch (error) {
      console.error("Error adding BYOB bundle to cart:", error);
      alert("Error adding bundle to cart. Please try again.");
    } finally {
      this.isSubmittingBundle = false;
      this.setATCButtonLoadingState(false);
      this.setAllButtonsLoadingState(false);
      this.updateATCButtonVisibility();
      this.updateProductsSwiperVisibility();
    }
  }

  public async addToBundle(
    stepIndex: number,
    productData: BundleProduct
  ): Promise<void> {
    const quantity =
      Number.isFinite(productData.quantity) && productData.quantity > 0
        ? productData.quantity
        : 1;

    this.bundleProducts = this.bundleProducts.slice(0, stepIndex);

    const availableSlots = this.maxSteps - stepIndex;
    const slotsToFill = Math.min(quantity, availableSlots);

    for (let i = 0; i < slotsToFill; i++) {
      this.bundleProducts[stepIndex + i] = {
        ...productData,
        quantity: 1,
      };
    }

    this.updateBundlePrices();
    this.updateStepTitles();

    const nextStep =
      stepIndex + slotsToFill < this.maxSteps
        ? stepIndex + slotsToFill
        : stepIndex + slotsToFill - 1;
    this.showStep(nextStep);

    this.updateATCButtonVisibility();
    this.updateProductsSwiperVisibility();
  }

  public removeStepSelection(stepIndex: number): void {
    const arrayIndex = stepIndex - 1;
    if (arrayIndex < 0 || arrayIndex >= this.bundleProducts.length) return;

    this.bundleProducts.splice(arrayIndex, 1);
    this.updateBundlePrices();
    this.updateStepTitles();
    const nextStep =
      this.bundleProducts.length < this.maxSteps
        ? this.bundleProducts.length
        : this.maxSteps - 1;
    this.showStep(nextStep);
    this.updateATCButtonVisibility();
    this.updateProductsSwiperVisibility();
  }

  public async initBundleContents(): Promise<void> {
    try {
      const r = await fetch("/cart.js", {
        headers: { Accept: "application/json" },
      });
      const data = await r.json();

      const byobItems = (data.items || []).filter(
        (item: any) => item.properties && item.properties.byob === "true"
      );

      if (byobItems.length === 0) {
        this.resetBundleUI();
        return;
      }

      this.bundleProducts = [];

      let step = 0;

      byobItems
        .slice()
        .sort((a: any, b: any) => {
          const av = parseInt(a?.properties?._bundle_item || "0", 10);
          const bv = parseInt(b?.properties?._bundle_item || "0", 10);
          return av - bv;
        })
        .forEach((item: any) => {
          if (step >= this.maxSteps) return;

          this.bundleProducts[step++] = {
            id: item.variant_id.toString(),
            title: item.title,
            price: (Number(item.original_price ?? item.price) / 100).toFixed(
              2
            ),
            quantity: item.quantity || 1,
            percentage_discount: item.properties?.percentage_discount || "0",
            inCart: true,
            image: item.image || "",
          };
        });

      this.showStep(Math.min(step, this.maxSteps - 1));
      this.updateBundlePrices();
      this.updateStepTitles();
      this.updateATCButtonVisibility();
      this.updateProductsSwiperVisibility();
    } catch (e) {
      console.error("initBundleContents error:", e);
    }
  }

  private setupEventListeners(): void {
    document.querySelectorAll(".byob-product-card__btn").forEach((btn) => {
      btn.addEventListener("click", () => {
        const stepIndex = this.bundleProducts.length;

        if (stepIndex >= this.maxSteps) {
          console.warn("Bundle already complete");
          return;
        }

        const id = btn.getAttribute("data-variant-id") || "";
        const title = btn.getAttribute("data-title") || "";
        const price = btn.getAttribute("data-price") || "";
        const percentage_discount = btn.getAttribute("data-diff") || "";

        const card = (btn as HTMLElement).closest(".product-card");
        const imgEl =
          (card?.querySelector("img") as HTMLImageElement | null) || null;
        const image = (
          btn.getAttribute("data-image") ||
          imgEl?.currentSrc ||
          imgEl?.src ||
          ""
        ).trim();

        const qtyInput = (btn as HTMLElement)
          .closest(".product-card")
          ?.querySelector<HTMLInputElement>(".byob-qty__input");

        const quantity = qtyInput ? parseInt(qtyInput.value || "1", 10) : 1;

        this.addToBundle(stepIndex, {
          id,
          title,
          price,
          percentage_discount,
          quantity,
          image,
        });

        this.scrollToStepsSection();
      });
    });

    if (this.atcButtonEl) {
      this.atcButtonEl.addEventListener("click", () => {
        this.submitBundleToCart();
      });
    }

    document.addEventListener("click", (e) => {
      const btn = (e.target as HTMLElement).closest(".byob-qty__btn");
      if (!btn) return;

      const wrapper = btn.closest(".byob-qty");
      if (!wrapper) return;

      const input = wrapper.querySelector<HTMLInputElement>(".byob-qty__input");
      if (!input) return;

      const current = parseInt(input.value || "1", 10);
      const action = btn.getAttribute("data-action");

      if (action === "increase") input.value = String(current + 1);
      if (action === "decrease" && current > 1)
        input.value = String(current - 1);
    });
  }

  private scrollToStepsSection(): void {
    const stepsElement = document.querySelector(".byob-page__content--steps");
    if (stepsElement) {
      const header =
        document.querySelector("header") || document.querySelector(".header");
      const headerHeight = header ? (header as HTMLElement).offsetHeight : 78;
      const marginOffset = 20;

      const elementTop =
        (stepsElement as HTMLElement).getBoundingClientRect().top +
        window.pageYOffset;
      const scrollPosition = Math.max(
        0,
        elementTop - headerHeight - marginOffset
      );

      window.scrollTo({
        top: scrollPosition,
        behavior: "smooth",
      });
    }
  }

  private setupGlobalMethods(): void {
    window.addToBundle = (stepIndex: number, productData: BundleProduct) => {
      return this.addToBundle(stepIndex, productData);
    };

    window.removeStepSelection = (stepIndex: number) => {
      return this.removeStepSelection(stepIndex);
    };

    window.initBundleContents = () => {
      return this.initBundleContents();
    };
  }
}

export function initBYOB(): BYOB | null {
  if (document.querySelectorAll(".byob-page__step").length > 0) {
    return new BYOB();
  }
  return null;
}

export default BYOB;

declare global {
  interface Window {
    addToBundle: (
      stepIndex: number,
      productData: BundleProduct
    ) => Promise<void>;
    removeStepSelection: (stepIndex: number) => void;
    initBundleContents: () => void;
    reinitializeCartGamification?: () => void;
    sideCartInstance?: {
      setupEventListeners: () => void;
    };
  }
}